/** Pure presentation for the firm-side Client assets tab: message ids, filtering and table rows. */
export type ClientAssetTypeKey = 'deliverable' | 'reference' | 'input_request' | 'client_response';
export type AssetFilter = 'all' | 'deliverable' | 'reference' | 'input_request';

export type ClientAssetLike = {
	id: string;
	assetType: string;
	name: string;
	status: string;
	archived: boolean;
	dueDate: string | null;
	taskName: string | null;
	currentVersion: {versionNo: number} | null;
	versions: ReadonlyArray<{versionNo: number}>;
	responses: ReadonlyArray<{id: string}>;
	readReceipts: ReadonlyArray<{email: string; versionNo: number; readAt: string}>;
};

export const typeMessageId = (t: string): string => `client_assets.type.${t}`;
export const statusMessageId = (a: Pick<ClientAssetLike, 'status' | 'archived'>): string =>
	a.archived ? 'client_assets.status.archived' : `client_assets.status.${a.status}`;

export const filterAssets = <T extends Pick<ClientAssetLike, 'assetType'>>(list: ReadonlyArray<T>, f: AssetFilter): T[] =>
	f === 'all' ? [...list] : list.filter(a => a.assetType === f);

export function formatBytes(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${Math.round((n / 1024) * 10) / 10} KB`;
	return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;
}

export type MessageRef = {id: string; values?: Record<string, string | number>};

export function engagementLine(a: ClientAssetLike): MessageRef | null {
	if (a.assetType === 'reference') {
		const r = a.readReceipts[0];
		return r ? {id: 'client_assets.engagement.read_by', values: {email: r.email, date: r.readAt.slice(0, 10)}} : null;
	}
	if (a.assetType === 'input_request') {
		if (a.versions.length > 0) return {id: 'client_assets.engagement.uploads', values: {count: a.versions.length}};
		return a.dueDate ? {id: 'client_assets.engagement.due', values: {date: a.dueDate}} : null;
	}
	return a.responses.length > 0 ? {id: 'client_assets.engagement.responses', values: {count: a.responses.length}} : null;
}

export type ClientAssetRow = {
	id: string;
	name: string;
	type: string;
	version: string;
	task: string;
	status: string;
	engagement: string;
};

/** Precomputed plain rows: the DS Table's TextColumn renders accessor strings only. */
export function toRows(
	list: ReadonlyArray<ClientAssetLike>,
	t: (id: string, values?: Record<string, string | number>) => string
): ClientAssetRow[] {
	return list.map(a => {
		const e = engagementLine(a);
		return {
			id: a.id,
			name: a.name,
			type: t(typeMessageId(a.assetType)),
			version: a.currentVersion ? `v${a.currentVersion.versionNo}` : '—',
			task: a.taskName ?? t('client_assets.project_level'),
			status: t(statusMessageId(a)),
			engagement: e ? t(e.id, e.values) : '—',
		};
	});
}

// ---- FOR-38 deliverable approval ----

export type AttentionKind = 'changes_requested' | 'received' | 'no_approver';
export type AttentionItem = {assetId: string; kind: AttentionKind; message: MessageRef; note: string | null};
export type AttentionAssetLike = Pick<ClientAssetLike, 'id' | 'assetType' | 'name' | 'status' | 'archived'> & {
	approverPortalUserId?: string | null;
	versions: ReadonlyArray<{versionNo: number; decisionNote?: string | null}>;
};

const ATTENTION_RANK: Record<AttentionKind, number> = {changes_requested: 0, received: 1, no_approver: 2};

/**
 * What the firm has to act on, derived from Project.clientAssets (no extra query), in a fixed
 * order: the client asked for changes (with the latest version's note), the client uploaded into
 * a request that waits for acknowledgement, a deliverable nobody can approve. Archived assets
 * never appear. Each asset yields at most one item, so assetId is a unique key.
 */
export function attentionItems(assets: ReadonlyArray<AttentionAssetLike>): AttentionItem[] {
	const items: AttentionItem[] = [];
	for (const a of assets) {
		if (a.archived) continue;
		const values = {name: a.name};
		if (a.assetType === 'deliverable' && a.status === 'changes_requested') {
			const latest = a.versions.reduce<AttentionAssetLike['versions'][number] | null>(
				(max, v) => (max === null || v.versionNo > max.versionNo ? v : max),
				null
			);
			items.push({
				assetId: a.id,
				kind: 'changes_requested',
				message: {id: 'client_assets.attention.changes_requested', values},
				note: latest?.decisionNote ?? null,
			});
		} else if (a.assetType === 'input_request' && a.status === 'received') {
			items.push({
				assetId: a.id,
				kind: 'received',
				message: {id: 'client_assets.attention.received', values},
				note: null,
			});
		} else if (a.assetType === 'deliverable' && a.status === 'awaiting_approval' && !a.approverPortalUserId) {
			items.push({
				assetId: a.id,
				kind: 'no_approver',
				message: {id: 'client_assets.attention.no_approver', values},
				note: null,
			});
		}
	}
	return items.sort((x, y) => ATTENTION_RANK[x.kind] - ATTENTION_RANK[y.kind]); // stable: list order within a kind
}

export type DecisionVersionLike = {decision?: string | null; decidedAt?: string | null; decidedByEmail?: string | null};

/**
 * One deliverable version's decision line in the drawer, or null. A decided version names the
 * decider (a dash once that user is deleted: decided_by has no FK, so the email join can be null).
 * Only the latest undecided version of a deliverable still awaiting approval reads "Awaiting {approver}".
 */
export function versionDecisionLine(
	v: DecisionVersionLike,
	ctx: {isLatest: boolean; status: string; approverEmail?: string | null}
): MessageRef | null {
	const email = v.decidedByEmail ?? '—';
	const date = v.decidedAt ? v.decidedAt.slice(0, 10) : '';
	if (v.decision === 'approved') return {id: 'client_assets.drawer.decision_approved', values: {email, date}};
	if (v.decision === 'changes_requested') return {id: 'client_assets.drawer.decision_changes', values: {email, date}};
	if (ctx.isLatest && ctx.status === 'awaiting_approval' && ctx.approverEmail) {
		return {id: 'client_assets.drawer.awaiting_decision', values: {email: ctx.approverEmail}};
	}
	return null;
}
